import React, { useCallback, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import {
  ForbiddenSection,
  withAuthorizationVerifier,
} from "../../components/authorization-verifier";
import { PropertyMapAPIPromiseClient } from "../../gen/witbooking/backoffice/propertymap/v1/propertymap_api_grpc_web_pb";
import {
  Coordinates,
  CreateOrReplacePropertyMapRequest,
  GeoLocation,
  GeoMap,
  GetPropertyMapRequest,
  Location,
  MapCategory,
  MapTranslation,
  PropertyMap,
  TickerReference,
} from "../../gen/witbooking/backoffice/propertymap/v1/propertymap_api_pb";
import actions from "../../lib/actions";
import { useUserAuthInfo } from "../../lib/auth";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { useHttpClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { JsonEditor } from "../shared/widgets/jsonEditor";
import { BASE_URL } from "../witadmin/request";
import schema from "./infrastructure/customMaps.schema.json";

export const customMapMessages = defineMessages({
  customMapsJsonLabel: {
    id: "customMaps.json.label",
    defaultMessage: "json",
  },
  customMapsJsonHelpText: {
    id: "customMaps.json.helpText",
    defaultMessage: "Please do not remove any property",
  },
});

const messages = {
  ...defaultCrudMessages,
  ...customMapMessages,
};

interface CustomGeoMap extends Omit<GeoMap.AsObject, "googleMapsConfig"> {
  googleMapsConfig: any;
}

interface CustomPropertyMap extends Omit<PropertyMap.AsObject, "mapsList"> {
  mapsList: CustomGeoMap[];
}

export interface CustomMap {
  id: string;
  response: CustomPropertyMap | {};
}

function buildFormConfiguration(): FormConfiguration<CustomMap> {
  return {
    __fieldOrder: ["response", "*"],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    response: {
      fieldWidget: JsonEditor,
      messages: {
        label: messages.customMapsJsonLabel,
        helpText: messages.customMapsJsonHelpText,
      },
    },
  };
}

function CustomMapsForm({ propertyTicker }: { propertyTicker: string }) {
  const userAuthInfo = useUserAuthInfo();
  const intl = useIntl();
  const httpClientCache = useHttpClient(BASE_URL || "");
  const grpcClient = useGrpcClient(PropertyMapAPIPromiseClient);
  const getMaps = usePropertyGrpcCall(grpcClient, grpcClient.getPropertyMap);
  const updatePropertyMaps = usePropertyGrpcCall(grpcClient, grpcClient.createOrReplacePropertyMap);

  const handleUpdates: (entity: CustomMap) => Promise<void> = useCallback(
    async entity => {
      const response = entity.response as PropertyMap.AsObject;
      const newPropertyMap = new PropertyMap();
      const requestPropertyMaps = new CreateOrReplacePropertyMapRequest();

      if (response.categoriesList && response.mapsList) {
        const newCategoriesList: MapCategory[] = response.categoriesList.map(category => {
          const newCategory = new MapCategory();
          newCategory.setTicker(category.ticker);
          newCategory.setType(category.type);
          newCategory.setStatus(category.status);
          newCategory.setColor(category.color);
          newCategory.setContrastText(category.contrastText);
          newCategory.setShowIconPrice(category.showIconPrice);
          newCategory.setIcon(category.icon);
          newCategory.setPolygonColor(category.polygonColor);
          if (category.mapTo) {
            const mapTo = new TickerReference();
            mapTo.setTickersList(category.mapTo.tickersList);
            newCategory.setMapTo(mapTo);
          }
          category.translationsList.forEach(translation => {
            const newTranslation = new MapTranslation();
            newTranslation.setLocale(translation.locale);
            newTranslation.setName(translation.name);
            newTranslation.setDescription(translation.description);
            newCategory.addTranslations(newTranslation);
          });
          return newCategory;
        });

        const newMapsList = response.mapsList.map(map => {
          const newMap = new GeoMap();
          newMap.setTicker(map.ticker);
          newMap.setGoogleMapsConfig(JSON.stringify(map.googleMapsConfig));
          newMap.setMaptype(map.maptype);

          map.locationsList.forEach(location => {
            const newLocation = new Location();
            newLocation.setCategory(location.category);
            newLocation.setStatus(location.status);

            if (location.geo) {
              const newGeo = new GeoLocation();
              newGeo.setType(location.geo.type);
              if (location.geo.positionIconList?.length) {
                const locations = new GeoLocation();
                const newPositionIcon = new Coordinates();
                newPositionIcon.setLat(location.geo.positionIconList[0].lat);
                newPositionIcon.setLng(location.geo.positionIconList[0].lng);
                newPositionIcon.setOrder(0);
                locations.addPositionIcon(newPositionIcon);

                newGeo.setPositionIconList(locations.getPositionIconList());
              }

              location.geo.valuesList.forEach((value, index) => {
                const newCoord = new Coordinates();
                newCoord.setLat(value.lat);
                newCoord.setLng(value.lng);
                newCoord.setOrder(value.order || index);
                newGeo.addValues(newCoord);
              });

              newLocation.setGeo(newGeo);
            }

            newMap.addLocations(newLocation);
          });

          map.translationsList.forEach(translation => {
            const newTranslation = new MapTranslation();
            newTranslation.setLocale(translation.locale);
            newTranslation.setName(translation.name);
            newTranslation.setDescription(translation.description);
            newMap.addTranslations(newTranslation);
          });

          return newMap;
        });
        newPropertyMap.setCategoriesList(newCategoriesList);
        newPropertyMap.setMapsList(newMapsList);
        requestPropertyMaps.setPropertymap(newPropertyMap);
      }
      console.log(requestPropertyMaps.toObject());
      await Promise.all([updatePropertyMaps(requestPropertyMaps)]);

      await regenPropertyCache(httpClientCache, propertyTicker);
      alert(intl.formatMessage(messages.saveSuccess));
    },
    [httpClientCache, intl, propertyTicker, updatePropertyMaps]
  );

  const formConfiguration = useMemo(() => buildFormConfiguration(), []);

  const handleGetAll = useCallback(async (): Promise<CustomMap> => {
    try {
      const request = new GetPropertyMapRequest();
      const getMapRequest = await getMaps(request);
      const response: PropertyMap.AsObject | {} = {
        mapsList:
          getMapRequest
            .getPropertymap()
            ?.toObject()
            .mapsList.map(map => ({
              ...map,
              googleMapsConfig: JSON.parse(map.googleMapsConfig),
            })) || {},
        categoriesList: getMapRequest.getPropertymap()?.toObject().categoriesList || {},
      };
      return {
        id: "customMaps",
        response,
      };
    } catch (error) {
      return {
        id: "customMaps",
        response: {},
      };
    }
  }, [getMaps]);

  if (!userAuthInfo) {
    return <div>Loading...</div>;
  }
  if (!userAuthInfo.isWitAdmin) {
    return <ForbiddenSection showMessage />;
  }

  return (
    <CrudForm<CustomMap>
      id={"customMaps"}
      schema={schema}
      formConfiguration={formConfiguration}
      messages={messages}
      onGet={handleGetAll}
      onUpdate={handleUpdates}
      canUpdate={actions.ModifyCustomMaps}
      enumsOptional
    />
  );
}

export default withAuthorizationVerifier(CustomMapsForm);
